import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import apiClient from '../api/client';
import toast from 'react-hot-toast';

const AuthContext = createContext(null);

const PAWASCHOOL_ROLES = [
  'student',
  'admin',
  'super_admin',
  'teacher',
  'parent',
];
const canUsePawaSchool = (user) =>
  !!user && PAWASCHOOL_ROLES.includes(user.role);

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error(
      'useAuth must be used inside AuthProvider'
    );
  }

  return context;
};

const extractUser = (response) => {
  const responseData = response?.data?.data || response?.data;
  return responseData?.user || responseData;
};

const decodeJwt = (jwtToken) => {
  try {
    const base64Url = jwtToken.split('.')[1];

    if (!base64Url) return null;

    const base64 = base64Url
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(
        Math.ceil(base64Url.length / 4) * 4,
        '='
      );

    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
};

const tokenExpiresWithin = (
  jwtToken,
  milliseconds = 0
) => {
  const payload = decodeJwt(jwtToken);

  if (!payload?.exp) return true;

  return (
    payload.exp * 1000 <=
    Date.now() + milliseconds
  );
};

const getErrorStatus = (error) =>
  error?.response?.status;

export const AuthProvider = ({ children }) => {
  const refreshPromiseRef = useRef(null);
  const hasBootstrappedRef = useRef(false);

  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem('user');

    if (!storedUser) return null;

    try {
      return JSON.parse(storedUser);
    } catch {
      localStorage.removeItem('user');
      return null;
    }
  });

  const [token, setToken] = useState(() =>
    localStorage.getItem('token')
  );

  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);
  const [planAccess, setPlanAccess] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('planAccess')) || null;
    } catch {
      localStorage.removeItem('planAccess');
      return null;
    }
  });
  const [planAccessLoading, setPlanAccessLoading] = useState(false);

  const saveUser = useCallback((nextUser) => {
    if (!nextUser) return;

    const schoolId =
      nextUser.school_id || nextUser.tenant_id;

    setUser(nextUser);
    localStorage.setItem(
      'user',
      JSON.stringify(nextUser)
    );

    if (schoolId) {
      localStorage.setItem('tenantId', schoolId);

      apiClient.defaults.headers.common['x-tenant-id'] =
        schoolId;
    }
  }, []);

  const clearAuth = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tenantId');
    localStorage.removeItem('user');
    localStorage.removeItem('subscription');
    localStorage.removeItem('selectedPlan');
    localStorage.removeItem('planAccess');

    delete apiClient.defaults.headers.common.Authorization;
    delete apiClient.defaults.headers.common['x-tenant-id'];

    setToken(null);
    setUser(null);
    setPlanAccess(null);
    setLoading(false);
    setAuthReady(true);
  }, []);

  // ============================================================
  // FIXED LOGOUT: Always redirect to the Landing Page.
  // ============================================================
  const logout = useCallback(() => {
    clearAuth();
    window.location.replace('/');
  }, [clearAuth]);

  const saveTokens = useCallback((accessToken, refreshToken) => {
    localStorage.setItem('token', accessToken);

    if (refreshToken) {
      localStorage.setItem(
        'refreshToken',
        refreshToken
      );
    }

    apiClient.defaults.headers.common.Authorization =
      `Bearer ${accessToken}`;

    setToken(accessToken);
  }, []);

  /*
   * Exchanges the permanent refresh token for a new short-lived
   * access token. Concurrent refresh attempts share one request.
   */
  const refreshSession = useCallback(async () => {
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const storedRefreshToken =
      localStorage.getItem('refreshToken');

    if (!storedRefreshToken) {
      const error = new Error(
        'Refresh token missing'
      );
      error.code = 'REFRESH_TOKEN_MISSING';
      throw error;
    }

    refreshPromiseRef.current = (async () => {
      const response = await apiClient.post(
        '/auth/refresh',
        {
          refreshToken: storedRefreshToken,
        }
      );

      const payload =
        response.data?.data || response.data;

      if (!payload?.accessToken) {
        throw new Error(
          'The server did not return a new access token.'
        );
      }

      saveTokens(
        payload.accessToken,
        payload.refreshToken
      );

      return payload.accessToken;
    })();

    try {
      return await refreshPromiseRef.current;
    } finally {
      refreshPromiseRef.current = null;
    }
  }, [saveTokens]);

  const refreshUser = useCallback(async (accessToken) => {
    const currentToken =
      accessToken || localStorage.getItem('token');

    if (!currentToken) {
      throw new Error('Authentication token missing');
    }

    apiClient.defaults.headers.common.Authorization =
      `Bearer ${currentToken}`;

    const response = await apiClient.get('/users/me');
    const freshUser = extractUser(response);

    if (!freshUser?.id) {
      throw new Error(
        'The server did not return valid user information.'
      );
    }

    if (!canUsePawaSchool(freshUser)) {
      const error = new Error(
        'This account cannot sign in to PawaSchool.'
      );
      error.code = 'ROLE_NOT_ALLOWED';
      throw error;
    }

    saveUser(freshUser);

    if (freshUser.subscription) {
      localStorage.setItem(
        'subscription',
        JSON.stringify(freshUser.subscription)
      );
    } else {
      localStorage.removeItem('subscription');
    }

    return freshUser;
  }, [saveUser]);

  const refreshPlanAccess = useCallback(async () => {
    if (!user?.school_id || user?.role === 'super_admin') {
      setPlanAccess(null);
      localStorage.removeItem('planAccess');
      return null;
    }

    try {
      setPlanAccessLoading(true);
      const response = await apiClient.get('/subscriptions/access');
      const access = response.data?.data || null;
      setPlanAccess(access);

      if (access) {
        localStorage.setItem('planAccess', JSON.stringify(access));
      } else {
        localStorage.removeItem('planAccess');
      }

      return access;
    } catch (error) {
      setPlanAccess(null);
      localStorage.removeItem('planAccess');
      throw error;
    } finally {
      setPlanAccessLoading(false);
    }
  }, [user?.school_id, user?.role]);

  const isSuperAdmin = useMemo(
    () => user?.role === 'super_admin',
    [user]
  );

  const isAdmin = useMemo(
    () =>
      user?.role === 'admin' ||
      user?.role === 'super_admin',
    [user]
  );

  const isTeacher = useMemo(
    () => user?.role === 'teacher',
    [user]
  );

  const isStudent = useMemo(
    () => user?.role === 'student',
    [user]
  );

  const isParent = useMemo(
    () => user?.role === 'parent',
    [user]
  );

  const hasValidSubscription = useMemo(() => {
    if (!user) return false;

    if (user.role === 'super_admin') {
      return true;
    }

    // Public PawaLibrary users do not need a school plan.
    if (
      !user.school_id &&
      user.role === 'library_user'
    ) {
      return true;
    }

    const subscription = planAccess?.subscription || user.subscription;
    const status = String(subscription?.status || '').toLowerCase();

    return (
      ['active', 'trial'].includes(status) &&
      (
        subscription?.isValid === true ||
        user.hasActiveSubscription === true ||
        user.subscription?.isValid === true
      )
    );
  }, [user, planAccess]);

  // Backward-compatible name used by existing route guards.
  const hasPaidPlan = hasValidSubscription;

  const planFeatures = planAccess?.features || {};
  const planLimits = planAccess?.limits || {};

  const hasFeature = useCallback((feature) => {
    if (user?.role === 'super_admin') return true;
    return planFeatures?.[feature] === true;
  }, [user?.role, planFeatures]);

  const canViewFeature = useCallback((feature) => {
    if (hasFeature(feature)) return true;
    return planFeatures.view_only === true || planFeatures.view_data === true;
  }, [hasFeature, planFeatures]);

  const getPlanLimit = useCallback((resource) => {
    const unlimitedKey = `unlimited_${resource}`;
    const maximumKey = `max_${resource}`;
    if (planLimits?.[unlimitedKey] === true || planFeatures?.[unlimitedKey] === true) {
      return null;
    }
    const value = planLimits?.[maximumKey] ?? planFeatures?.[maximumKey];
    return value === null || value === undefined ? null : Number(value);
  }, [planLimits, planFeatures]);

  useEffect(() => {
    if (!authReady || !token || !user?.school_id || user?.role === 'super_admin') {
      return;
    }

    refreshPlanAccess().catch((error) => {
      if (![401, 403].includes(getErrorStatus(error))) {
        console.error('Plan access load error:', error);
      }
    });
  }, [authReady, token, user?.school_id, user?.role, refreshPlanAccess]);

  const login = useCallback(
    async (email, password) => {
      try {
        const response = await apiClient.post(
          '/auth/login',
          {
            email: email.trim().toLowerCase(),
            password,
            app: 'pawaschool',
          }
        );

        const payload =
          response.data?.data || response.data;

        const accessToken = payload?.accessToken;
        const refreshToken = payload?.refreshToken;
        const loginUser = payload?.user;
        const subscription = payload?.subscription;

        if (!accessToken || !loginUser) {
          toast.error('Invalid response from the server.');

          return {
            success: false,
            message: 'Invalid server response',
          };
        }

        if (!canUsePawaSchool(loginUser)) {
          return {
            success: false,
            message: 'This account cannot sign in to PawaSchool.',
          };
        }

        const completeUser = {
          ...loginUser,
          subscription:
            subscription ||
            loginUser.subscription ||
            null,
          hasActiveSubscription:
            loginUser.hasActiveSubscription === true ||
            subscription?.isValid === true,
        };

        const schoolId =
          completeUser.school_id ||
          completeUser.tenant_id;

        saveTokens(accessToken, refreshToken);

        if (schoolId) {
          localStorage.setItem(
            'tenantId',
            schoolId
          );

          apiClient.defaults.headers.common[
            'x-tenant-id'
          ] = schoolId;
        }

        saveUser(completeUser);
        setAuthReady(true);
        setLoading(false);

        const subscriptionStatus = String(
          completeUser.subscription?.status || ''
        ).toLowerCase();

        const requiresSubscription =
          completeUser.role !== 'super_admin' &&
          !!completeUser.school_id &&
          (
            payload?.subscriptionRequired === true ||
            !['active', 'trial'].includes(subscriptionStatus) ||
            completeUser.subscription?.isValid !== true
          );

        return {
          success: true,
          subscriptionRequired:
            requiresSubscription,
          redirect: requiresSubscription
            ? '/subscribe_to_plan'
            : '/app/dashboard',
          data: payload,
        };
      } catch (error) {
        const response = error.response?.data;
        const message =
          response?.message ||
          'Login failed. Please try again.';

        // Legacy backend response containing a token.
        if (
          response?.subscriptionRequired &&
          response?.data?.accessToken
        ) {
          const payload = response.data;

          saveTokens(
            payload.accessToken,
            payload.refreshToken
          );
          saveUser(payload.user);
          setAuthReady(true);

          return {
            success: true,
            subscriptionRequired: true,
            redirect: '/subscribe_to_plan',
          };
        }

        toast.error(message);

        return {
          success: false,
          message,
        };
      }
    },
    [saveTokens, saveUser]
  );

  /*
   * Restore the session when the app starts or when a refreshed
   * access token is received. An expired access token is silently
   * replaced before /users/me is requested.
   */
  useEffect(() => {
    let active = true;

    const loadUser = async () => {
      const isFirstLoad =
        !hasBootstrappedRef.current;

      if (isFirstLoad) {
        setLoading(true);
      }

      const tenantId =
        localStorage.getItem('tenantId');

      const storedRefreshToken =
        localStorage.getItem('refreshToken');

      let currentToken =
        localStorage.getItem('token') || token;

      if (!currentToken && !storedRefreshToken) {
        if (active) {
          setUser(null);
          setLoading(false);
          setAuthReady(true);
          hasBootstrappedRef.current = true;
        }

        return;
      }

      if (tenantId) {
        apiClient.defaults.headers.common[
          'x-tenant-id'
        ] = tenantId;
      }

      try {
        // Refresh first when the app was closed long enough for the
        // one-hour access token to expire.
        if (
          !currentToken ||
          tokenExpiresWithin(currentToken, 30000)
        ) {
          currentToken = await refreshSession();
        }

        const freshUser = await refreshUser(
          currentToken
        );

        if (active) {
          setUser(freshUser);
        }
      } catch (error) {
        const status = error.response?.status;
        const response = error.response?.data;

        console.warn(
          'Could not refresh authenticated user:',
          response || error.message
        );

        if (status === 401) {
          try {
            // The access token may have expired between the first
            // check and the profile request. Refresh and retry once.
            const newAccessToken =
              await refreshSession();

            const freshUser = await refreshUser(
              newAccessToken
            );

            if (active) {
              setUser(freshUser);
            }
          } catch (refreshError) {
            if (
              getErrorStatus(refreshError) === 401 ||
              refreshError.code ===
                'REFRESH_TOKEN_MISSING'
            ) {
              clearAuth();
            }
            // Network/server failures preserve the cached login.
          }
        } else if (
          status === 402 ||
          response?.subscriptionRequired
        ) {
          // Preserve login. SubscriptionGuard handles redirect.
          console.log(
            'Subscription required — login session preserved.'
          );
        } else if (status === 403) {
          // A permission error must not automatically delete tokens.
          console.log(
            'Access denied — login session preserved.'
          );
        }
      } finally {
        if (active) {
          setLoading(false);
          setAuthReady(true);
          hasBootstrappedRef.current = true;
        }
      }
    };

    loadUser();

    return () => {
      active = false;
    };
  }, [
    token,
    refreshUser,
    refreshSession,
    clearAuth,
  ]);

  // Renew the access token five minutes before it expires.
  useEffect(() => {
    if (!token) return undefined;

    const payload = decodeJwt(token);

    if (!payload?.exp) {
      return undefined;
    }

    const expiresAt = payload.exp * 1000;
    const remainingTime = expiresAt - Date.now();

    const refreshAfter = remainingTime <= 0
      ? 0
      : Math.max(
          1000,
          remainingTime - 5 * 60 * 1000
        );

    const timer = window.setTimeout(async () => {
      try {
        await refreshSession();
      } catch (error) {
        if (
          getErrorStatus(error) === 401 ||
          error.code === 'REFRESH_TOKEN_MISSING'
        ) {
          toast.error(
            'Your login is no longer valid. Please sign in again.'
          );

          clearAuth();
        }
      }
    }, refreshAfter);

    return () => {
      window.clearTimeout(timer);
    };
  }, [token, refreshSession, clearAuth]);

  /*
   * Browsers pause timers while a device sleeps or a tab is in the
   * background. Check the session again on focus, visibility and
   * reconnection so returning users are never sent to the login page.
   */
  useEffect(() => {
    const maintainSession = async () => {
      if (
        document.visibilityState === 'hidden' ||
        !localStorage.getItem('refreshToken')
      ) {
        return;
      }

      const currentToken =
        localStorage.getItem('token');

      if (
        currentToken &&
        !tokenExpiresWithin(
          currentToken,
          5 * 60 * 1000
        )
      ) {
        return;
      }

      try {
        await refreshSession();
      } catch (error) {
        if (getErrorStatus(error) === 401) {
          clearAuth();
        }
        // Stay logged in during temporary offline/server failures.
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        maintainSession();
      }
    };

    window.addEventListener('focus', maintainSession);
    window.addEventListener('online', maintainSession);
    document.addEventListener(
      'visibilitychange',
      handleVisibilityChange
    );

    return () => {
      window.removeEventListener(
        'focus',
        maintainSession
      );
      window.removeEventListener(
        'online',
        maintainSession
      );
      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange
      );
    };
  }, [refreshSession, clearAuth]);

  const isAuthenticated =
    authReady && !!token && !!user;

  const value = useMemo(
    () => ({
      user,
      setUser: saveUser,
      token,
      loading,
      authReady,
      isAuthenticated,
      hasPaidPlan,
      hasValidSubscription,
      planAccess,
      planAccessLoading,
      planFeatures,
      planLimits,
      hasFeature,
      canViewFeature,
      getPlanLimit,
      isAdmin,
      isSuperAdmin,
      isTeacher,
      isStudent,
      isParent,
      login,
      logout,
      clearAuth,
      refreshUser,
      refreshSession,
      refreshPlanAccess,
    }),
    [
      user,
      saveUser,
      token,
      loading,
      authReady,
      isAuthenticated,
      hasPaidPlan,
      hasValidSubscription,
      planAccess,
      planAccessLoading,
      planFeatures,
      planLimits,
      hasFeature,
      canViewFeature,
      getPlanLimit,
      isAdmin,
      isSuperAdmin,
      isTeacher,
      isStudent,
      isParent,
      login,
      logout,
      clearAuth,
      refreshUser,
      refreshSession,
      refreshPlanAccess,
    ]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;